import React, { useMemo } from 'react';
import { Platform } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import * as DropdownMenu from '@/components/DropdownMenu/DropdownMenu';
import DropdownMenuItem from '@/components/DropdownMenu/DropdownMenuItem';
import useResponsiveness from '@/hooks/useResponsiveness';
import { useTranslation } from '@/hooks/useTranslation';

function renderWebItems(items) {
  return items.map((item) => (
    <DropdownMenuItem
      key={item.key}
      onSelect={() => item.onSelect?.()}
      className="DropdownMenuItem"
      icon={item.icon}
      title={item.title}
    />
  ));
}

function renderNativeItems(items, colors) {
  return items.map((item) => (
    <DropdownMenu.Item
      key={item.key}
      onSelect={() => item.onSelect?.()}
    >
      <DropdownMenu.ItemIcon ios={{
        name: item.iosIcon,
        color: colors.text_color_primary,
      }} />
      <DropdownMenu.ItemTitle>
        {item.title}
      </DropdownMenu.ItemTitle>
    </DropdownMenu.Item>
  ));
}

function MemoryActionsDropdown({
  trigger,
  onOpenBuilder,
  onOpenImport,
  onExport,
  canExport = false,
  exportLabel,
}) {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const { isXS } = useResponsiveness();

  const setupItems = useMemo(() => [
    {
      key: 'guided-setup',
      icon: 'wand-magic-sparkles',
      iosIcon: 'wand.and.stars',
      title: t('pages.memory.guidedSetup'),
      onSelect: onOpenBuilder,
    },
    {
      key: 'import',
      icon: 'file-import',
      iosIcon: 'square.and.arrow.down',
      title: t('pages.memory.importFromAI'),
      onSelect: onOpenImport,
    },
    canExport && {
      key: 'export',
      icon: 'copy',
      iosIcon: 'doc.on.doc',
      title: exportLabel || t('pages.memory.exportMemories'),
      onSelect: onExport,
    },
  ].filter(Boolean), [canExport, exportLabel, onExport, onOpenBuilder, onOpenImport, t]);

  return (
    <DropdownMenu.Root>
      {trigger && (
        <DropdownMenu.Trigger asChild={Platform.OS === 'web' || Platform.OS === 'macos'}>
          {trigger}
        </DropdownMenu.Trigger>
      )}
      <DropdownMenu.Content
        className={Platform.OS === 'web' ? 'DropdownMenuContent' : undefined}
        align={isXS ? 'center' : 'end'}
        sideOffset={isXS ? 4 : 5}
      >
        {Platform.OS === 'web' ? renderWebItems(setupItems) : renderNativeItems(setupItems, colors)}
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
}

export default MemoryActionsDropdown;
